/**
 * Floating autocomplete menu for slash commands. Appears above the chat input
 * when the user types `/` at the start of a fresh prompt. Keyboard model:
 *
 *   ↑/↓     — move selection
 *   Enter   — apply selected command (parent re-uses the existing Enter
 *             handler via `onSelect`)
 *   Tab     — apply selected command without sending
 *   Escape  — close the menu (caller clears filter state)
 */
import { useEffect, useRef } from "react"

import { useTranslation } from "react-i18next"

import type { SlashCommand } from "./slash-commands"

import { cn } from "@/lib/utils"

import { getFilteredCommands } from "./slash-command-menu-utils"

interface SlashCommandMenuProps {
  filter: string
  activeIndex: number
  onHover: (index: number) => void
  onSelect: (command: SlashCommand) => void
}

export function SlashCommandMenu({
  filter,
  activeIndex,
  onHover,
  onSelect,
}: SlashCommandMenuProps) {
  const { t } = useTranslation()
  const items = getFilteredCommands(filter)
  const listRef = useRef<HTMLUListElement | null>(null)
  const activeItemRef = useRef<HTMLLIElement | null>(null)

  // Keep the active item visible when the user arrow-keys past the edge.
  useEffect(() => {
    const el = activeItemRef.current
    if (el) el.scrollIntoView({ block: "nearest" })
  }, [activeIndex])

  if (items.length === 0) return null

  return (
    <div
      aria-label={t("deepExplorer.slash_suggestions_aria")}
      role="listbox"
      className={cn(
        "bg-popover text-popover-foreground absolute bottom-full left-0 z-40 mb-2",
        "border-border/80 w-full max-w-[360px] overflow-hidden rounded-md border",
        "dark:bg-background shadow-lg ring-1 ring-black/5 dark:shadow-2xl dark:ring-white/10",
        "animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-1"
      )}
    >
      <ul ref={listRef} className="max-h-64 overflow-y-auto py-1 text-sm">
        {items.map((cmd, i) => {
          const Icon = cmd.icon
          const active = i === activeIndex
          return (
            <li
              key={cmd.name}
              ref={active ? activeItemRef : null}
              aria-selected={active}
              role="option"
              className={cn(
                "mx-1 flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 transition-colors",
                active
                  ? "bg-accent text-accent-foreground dark:bg-muted dark:text-foreground"
                  : "hover:bg-accent/60 dark:hover:bg-muted/60"
              )}
              // Use onMouseDown rather than onClick so the textarea doesn't
              // lose focus before selection completes (which would hide the
              // menu on the focus-out handler and cancel the click).
              onMouseEnter={() => onHover(i)}
              onMouseDown={(e) => {
                e.preventDefault()
                onSelect(cmd)
              }}
            >
              <Icon className="text-muted-foreground size-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-foreground font-mono text-[13px] font-medium">
                    /{cmd.name}
                  </span>
                  {cmd.aliases?.map((alias) => (
                    <span key={alias} className="text-muted-foreground font-mono text-xs">
                      /{alias}
                    </span>
                  ))}
                  {cmd.badgeKey && (
                    <span className="border-primary/30 bg-primary/10 text-primary rounded-full border px-1.5 py-px text-[10px] leading-none font-semibold">
                      {t(cmd.badgeKey)}
                    </span>
                  )}
                  <span className="text-muted-foreground truncate text-xs">{t(cmd.labelKey)}</span>
                </div>
                <p className="text-muted-foreground truncate text-xs">{t(cmd.descriptionKey)}</p>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
